<template>
  <div class="document-list">
    <!-- documents list -->
    <div
      :class="{ 'i-pb-15': hasPagination }"
      class="documents-container d-flex justify-content-center"
    >
      <div v-if="getDocumentsLoading" class="i-p-5">
        <i-spinner size="xl" />
      </div>
      <div
        v-else
        class="d-flex flex-column flex-grow-1 justify-content-center position-relative"
      >
        <!-- documents list for agent users -->
        <div
          v-if="!listIsEmpty && !filesBeingDragged"
          class="document-cards-list i-mb-11"
          data-testid="document-card-list"
        >
          <document-card
            v-for="document in documents"
            :key="document.id"
            :document="document"
            :loading="isDocumentLoading(document)"
            :return-to="returnToPath"
            @on-right-icon-click="viewDocument(document, true)"
            @on-document-click="viewDocument(document, false)"
            data-testid="document-card"
            data-dd-action-name="document-list-view-document-card"
          >
            <template #right-action>
              <document-item-menu
                :doc="document"
                @document-deleted="onDocumentDeleted"
              ></document-item-menu>
            </template>
          </document-card>
        </div>

        <!-- pagination -->
        <div
          v-if="hasPagination"
          data-testid="documents-pagination"
          class="pagination-wrapper d-flex flex-row align-items-center justify-content-center i-py-10"
        >
          <i-pagination
            size="md"
            :total-items="pagination.total"
            :items-per-page="pagination.limit"
            :selected-page="currentPage"
            @page-click="onPageClick"
          ></i-pagination>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DocumentCard from '@/V2/components/documents/DocumentCard';
import DocumentItemMenu from '@/V2/documents/document-list/DocumentItemMenu';
import { iButton, iIcon, iPagination, iSpinner } from '@inscribe/signal';
import { mapGetters, mapMutations } from 'vuex';
import * as documentStates from '@/constants/documentStates';

export default {
  components: {
    DocumentCard,
    DocumentItemMenu,
    iButton,
    iIcon,
    iSpinner,
    iPagination,
  },
  props: {
    selectedCustomer: {
      type: Object,
      default: () => {},
    },
    selectedFilter: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      currentPage: null,
      filesBeingDragged: false,
      uploadingDocuments: [],
    };
  },
  computed: {
    ...mapGetters({
      getDocumentsLoading: 'getDocumentsLoading',
      someDocumentUploading: 'someDocumentUploading',
    }),
    documents() {
      return this.$store.state.documents;
    },
    listIsEmpty() {
      return (
        this.$store.state.documents.length === 0 &&
        !this.$store.state.documentsLoading
      );
    },
    pagination() {
      const { pagination } = this.$store.state.documentsMetadata || {};
      return pagination;
    },
    hasPagination() {
      return this.pagination && this.pagination.number_of_pages > 1;
    },
    paginationDisabled() {
      return this.someDocumentUploading();
    },
    returnToPath() {
      return this.$route.fullPath;
    },
  },
  mounted() {
    this.currentPage = parseInt(this.$route?.query?.page) || 1;
  },
  methods: {
    ...mapMutations(['setDocuments', 'deleteUploadingDocWithName']),
    onPageClick(page) {
      this.currentPage = page;
      this.$router
        .push({
          path: this.$route.path,
          query: { page: this.currentPage },
        })
        .catch(() => {});
    },
    viewDocument(document) {
      this.$router.push({
        name: 'document-signals',
        params: {
          customer_id: document.customer_id,
          document_id: document.id,
        },
        query: {
          return_to: this.returnToPath,
        },
      });
    },
    isDocumentLoading(document) {
      return (
        document.state === documentStates.UPLOADING ||
        document.state === documentStates.PROCESSING
      );
    },
    onDocumentDeleted(docData) {
      this.$emit('document-deleted', docData);
    },
  },
};
</script>

<style lang="scss" scoped>
.document-list {
  align-items: center;
  $border-radius: 16px;

  .i-spinner {
    margin-top: 100px;
  }

  .documents-container {
    display: block;
  }

  .pagination-wrapper {
    $menu-width-s: 64px;
    $menu-width-l: 240px;
    $detail-sidebar-width-l: 225px;

    box-shadow: $box-shadow-default;
    background-color: $color-white;
    width: calc(100% - #{$detail-sidebar-width-l} - #{$menu-width-l});
    left: $menu-width-l;
    position: fixed;
    bottom: 0;
    z-index: 0;

    .i-pagination {
      width: 1200px;
      margin: auto;
    }

    @media screen and (max-width: 1100px) {
      left: $menu-width-s;
      width: calc(100% - #{$detail-sidebar-width-l} - #{$menu-width-s});
    }

    @media screen and (max-width: 768px) {
      left: 0;
      width: calc(100% - #{$detail-sidebar-width-l});
    }
  }
}
</style>
